import type { CSSProperties, ElementType, ReactNode } from "react"

import { withPalette, type Palette } from "@/components/marketplace/palette"
import { ArrowIcon, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"

// The signature affordance: an ink disc that reads as "enter this world" on every
// clickable tile, white arrow so it reads on any photo. Wayfinding stays neutral —
// blue is reserved for savings and primary CTAs.
const WorldArrow = ({ size }: { size: "sm" | "md" }) => (
  <span
    aria-hidden
    className={`bg-nelo-black-soft text-fg-inverted duration-quick ease-productive absolute z-10 grid place-items-center rounded-full transition-transform motion-safe:group-hover:translate-x-0.5 ${
      size === "sm" ? "top-200 right-200 size-7" : "top-300 right-300 size-9"
    }`}
  >
    <ArrowIcon size={size === "sm" ? 13 : 16} />
  </span>
)

interface PhotoTileProps {
  image: string
  title: string
  eyebrow?: string
  href?: string
  palette?: Palette
  /** Tailwind aspect utility — callers own the shape their grid needs. */
  aspect?: string
  size?: "sm" | "md"
  sizes?: string
  arrow?: boolean
  onClick?: () => void
  className?: string
  style?: CSSProperties
  children?: ReactNode
}

// Photography is the layout: a full-colour editorial photo edge to edge, its label
// on a bottom scrim rather than floating on the image. One object shared by the
// bento, the collection lanes and the search overlay, so a department reads the
// same everywhere instead of being a photo here and a pastel glyph there.
export const PhotoTile = ({
  image,
  title,
  eyebrow,
  href,
  palette,
  aspect = "aspect-square",
  size = "md",
  sizes = "50vw",
  arrow = true,
  onClick,
  className,
  style,
  children,
}: PhotoTileProps) => {
  const Tile: ElementType = href ? Link : "div"
  return (
    <Tile
      {...(href ? { href, onClick } : {})}
      style={style}
      className={withPalette(
        palette,
        `press rounded-12x focus-visible:ring-palette-bar group relative overflow-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${aspect} ${className ?? ""}`
      )}
    >
      <TileImage src={image} alt={title} fill sizes={sizes} className="duration-moderate ease-productive object-cover transition-transform motion-safe:group-hover:scale-105" />
      {href && arrow && <WorldArrow size={size} />}
      <div className={`bg-scrim-label absolute inset-x-0 bottom-0 flex flex-col gap-50 ${size === "sm" ? "p-250 pt-600" : "p-300 pt-1200 @xl:p-400 @xl:pt-1200"}`}>
        {eyebrow && (
          <Text as="span" variant="eyebrow" tone="inverted" className="opacity-85">
            {eyebrow}
          </Text>
        )}
        <span className={`font-display text-fg-inverted font-extrabold text-balance ${size === "sm" ? "text-body-sm @xl:text-body-lg" : "text-heading-sm @xl:text-heading-md"}`}>
          {title}
        </span>
        {children}
      </div>
    </Tile>
  )
}
